import React from 'react';
import './StupidSystemDesigns.css';

const elasticSearchDesignCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

class ElasticSearch {
    int idCounter = 1;
    unordered_map<int, string> docStore;
    unordered_map<int, int> docLengths;
    unordered_map<string, vector<pair<int,int>>> indexMap;
    
    unordered_set<string> stopWords = {"a", "an", "the", "is", "in", "it", "of", "or", "and", "to", "for", "with", "on", "at", "by", "this", "are"};

    void processContent(int docId, string content) {
        vector<string> tokens = getTokens(content);
        docLengths[docId] = tokens.size();

        unordered_map<string, int> freqMap = getFreqMap(tokens);
        for(auto &pair : freqMap) {
            indexMap[pair.first].push_back({docId, pair.second});
        }
    }
   
    vector<string> getTokens(string content) {
        vector<string> tokens;
        string word = "";
        for(char c : content) {
            if(isalnum(c)) word += tolower(c);
            else {
                if(word != "") tokens.push_back(word);
                word = "";
            }
        }
        if(word != "") tokens.push_back(word);
        return tokens;
    }
   
    unordered_map<string, int> getFreqMap(vector<string> tokens) {
        unordered_map<string, int> freqMap;
        for(auto &token : tokens) {
            if(stopWords.find(token) == stopWords.end()) {
                freqMap[token]++;
            }
        }
        return freqMap;
    }

public:
    ElasticSearch() {}
  
    void insert(string content) {
        int docId = idCounter++;
        docStore[docId] = content;
        processContent(docId, content);
    }
  
    
    vector<pair<int, double>> search(string query) {
        vector<string> rawTokens = getTokens(query);
        unordered_map<string, int> queryFreq = getFreqMap(rawTokens);    
        int numQueryTokens = queryFreq.size();
        if (numQueryTokens == 0) return {};

        int totalDocs = docStore.size();
        unordered_map<int, int> matchCount;
        unordered_map<int, double> docScores;

        for(auto &queryPair : queryFreq) {
            string token = queryPair.first;
            
            if(indexMap.find(token) == indexMap.end()) continue;

            // Calculate IDF: log(Total Docs / Docs containing term)
            double idf = log((double)totalDocs / indexMap[token].size()) + 1.0;

            for(auto &ids : indexMap[token]) {
                int docId = ids.first;
                int rawFreq = ids.second;
                
                // Calculate TF: (Term count in doc / Total words in doc)
                double tf = (double)rawFreq / docLengths[docId];
                
                docScores[docId] += (tf * idf);
                matchCount[docId]++;
            }
        }

        vector<pair<int, double>> results;
        for(auto &pair : matchCount) {
            // Only include documents that matched ALL valid query words
            if(pair.second == numQueryTokens) {
                results.push_back({pair.first, docScores[pair.first]});
            }
        }

        // Sort descending by score
        sort(results.begin(), results.end(), [](const pair<int, double>& a, const pair<int, double>& b) {
            return a.second > b.second;
        });

        return results;
    }
    
    string getDoc(int id) {
        return docStore[id];
    }
};

int main() {
    ElasticSearch es;
    es.insert("The quick brown fox jumps over the lazy dog.");
    es.insert("Fast search engines are quick and easy.");
    es.insert("The dog was quick, but the fox was extremely quick and fast.");

    cout << "Searching for: 'The quick fox'\n";
    cout << "--------------------------------\n";
    
    vector<pair<int, double>> results = es.search("The quick fox");
    
    for(auto &res : results) {
        cout << "DocID: " << res.first 
             << " | Score: " << fixed << setprecision(4) << res.second 
             << " | Text: " << es.getDoc(res.first) << "\n";
    }

    return 0;
}
`;

function ElasticSearchDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Elasticsearch Design</h1>
                            <p className="system-designs-subtitle">
                                A search system designed to find relevant content quickly instead of scanning every document one by one.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Look up content quickly for a user's search query.</li>
                                <li>Return documents that match the query terms.</li>
                                <li>Rank matching documents by relevance instead of returning them in storage order.</li>
                                <li>Support large collections without scanning every document for every request.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Idea</h2>
                            <p className="system-design-description">
                                Elasticsearch avoids searching every document from scratch. When a document is indexed, its content is analyzed into terms and those terms are recorded in a reverse index, also called an inverted index.
                            </p>
                            <p className="system-design-description">
                                Instead of mapping a document to all of its words, the reverse index maps each word to the documents that contain it. For example, the term <strong>database</strong> might point directly to documents 12, 48, and 91. A query can then jump to a small candidate set and combine the matching lists instead of scanning the whole collection.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Reverse Indexing</h2>
                            <ul>
                                <li><strong>Analyze:</strong> Split text into terms, normalize case, and optionally remove stop words or apply stemming.</li>
                                <li><strong>Index:</strong> Store each term with the document IDs where it appears.</li>
                                <li><strong>Query:</strong> Look up each query term in the reverse index.</li>
                                <li><strong>Combine:</strong> Intersect or union the document lists depending on the query semantics.</li>
                                <li><strong>Rank:</strong> Score the candidates and return the most relevant results first.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>TF-IDF Scoring</h2>
                            <p className="system-design-description">
                                Matching documents are scored with TF-IDF. Term frequency (TF) measures how often a query term appears in a document. Inverse document frequency (IDF) gives more weight to terms that appear in fewer documents, because those terms carry more information. The final score combines the TF and IDF values for the query terms found in each document.
                            </p>
                            <p className="system-design-description">
                                A document that contains the query terms often, especially terms that are uncommon across the collection, receives a higher score. Results are sorted by this score so the most relevant content appears first.
                            </p>
                            <p className="system-design-description">
                                TF-IDF is a useful and understandable model for this design. Modern Elasticsearch uses BM25 by default, which improves ranking with term-frequency saturation and document-length normalization, but the reverse-index lookup flow remains the important foundation.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Why This Is Faster</h2>
                            <p className="system-design-description">
                                A linear scan checks every document for every query, so the work grows directly with the size of the collection. A reverse index pays the indexing cost once and lets future queries jump directly to documents associated with their terms. The query still has work to do, especially for common terms, but it avoids repeatedly reading unrelated documents.
                            </p>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Working Logic</h2>
                            </div>
                            <pre className="design-code-block"><code>{elasticSearchDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        I saw a post on reddit saying "I made a search engine worse than elastic search". The top voted comment said: "That must have been difficult" HAHAAAAAA
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default ElasticSearchDesign;